import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import { manufacturerDataKeys } from '@/features/manufacturer-data/query-keys'

export type OrderFinding = components['schemas']['ManufacturerOrderFindingRead']
export type OrderFindingEvidence = components['schemas']['OrderFindingEvidenceRead']
export type OrderFindingField = components['schemas']['OrderFindingFieldRead']
export type OrderFindingRow = components['schemas']['ManufacturerOrderFindingListRead']
export type OrderReview = components['schemas']['ManufacturerOrderReviewRead']

export type OrderFindingParams = {
  status?: 'open' | 'applied' | 'dismissed' | 'all'
  kind?: string
  manufacturer_uuid?: string
  search?: string
  page?: number
  limit?: number
}

export type OrderReviewParams = {
  week_of?: string
  result?: 'changes' | 'matched' | 'failed' | 'all'
  manufacturer_uuid?: string
  search?: string
  page?: number
  limit?: number
}

/** The weekly order review (USA-1513). Under "projects" so the Action Items list refreshes with a decision. */
export const orderReviewKeys = {
  all: ['projects', 'order-review'] as const,
  finding: (uuid: string) => ['projects', 'order-review', 'finding', uuid] as const,
  findings: (params: OrderFindingParams) => ['projects', 'order-review', 'findings', params] as const,
  reviews: (params: OrderReviewParams) => ['projects', 'order-review', 'reviews', params] as const,
  weeks: ['projects', 'order-review', 'weeks'] as const,
  manufacturers: ['projects', 'order-review', 'manufacturers'] as const,
}

export function useOrderFinding(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: orderReviewKeys.finding(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-order-findings/{finding_uuid}', {
        params: { path: { finding_uuid: uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

export function useOrderFindings(params: OrderFindingParams, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: orderReviewKeys.findings(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-order-findings', { params: { query: params } })
      if (error) throw error
      return data
    },
    enabled,
  })
}

export function useOrderReviews(params: OrderReviewParams, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: orderReviewKeys.reviews(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-order-reviews', { params: { query: params } })
      if (error) throw error
      return data
    },
    enabled,
  })
}

export function useOrderReviewWeeks() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: orderReviewKeys.weeks,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-order-reviews/weeks')
      if (error) throw error
      return data
    },
  })
}

export function useOrderReviewManufacturers() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: orderReviewKeys.manufacturers,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-order-review/manufacturers')
      if (error) throw error
      return data
    },
  })
}

/** Approve (with staff's edited values) or dismiss a finding; the page shows the decision once it lands. */
export function useDecideOrderFinding(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      decision:
        | { kind: 'approve'; values?: Record<string, string | null>; notes?: string }
        | { kind: 'dismiss'; notes?: string },
    ) => {
      const client = await getClient()
      const params = { path: { finding_uuid: uuid } }
      const { data, error } =
        decision.kind === 'approve'
          ? await client.POST('/dashboard/manufacturer-order-findings/{finding_uuid}/approve', {
              params,
              body: { values: decision.values, notes: decision.notes || null },
            })
          : await client.POST('/dashboard/manufacturer-order-findings/{finding_uuid}/dismiss', {
              params,
              body: { notes: decision.notes || null },
            })
      if (error) throw error
      return data
    },
    onSuccess: (finding) => {
      queryClient.setQueryData(orderReviewKeys.finding(uuid), finding)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: orderReviewKeys.all })
      void queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.all })
      void queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.actionsAll })
    },
  })
}
